/**
 * Relay configuration form domain layer — shared between the site-dashboard
 * RelayBlade and the legacy EditSchedulesDialog.
 *
 * Owns the snapshot→form translation (relay initial values, schedule
 * defaults), the save-payload diff builder for POST /site-relay-config/,
 * the panel-type-7/8 relay pairing rules, and the sun-offset validation
 * rules. Pure functions only — no React, no IO.
 *
 * The two consumers historically drifted; the drift is kept explicit via
 * options rather than silently normalized:
 * - `useLooseEquality` — EditSchedulesDialog diffs fields with `==`,
 *   RelayBlade with `===` (differs when Formik text inputs hold string
 *   digits against numeric initial values).
 * - `assignPairedLoadsToPrimary` — EditSchedulesDialog re-homes a paired
 *   secondary relay's loads onto its primary; RelayBlade leaves them on
 *   the (hidden) secondary.
 * - `includePolarity` / `values.relayMonitor` presence — relay-monitor
 *   fields are RelayBlade-only; absent inputs add no payload keys.
 *
 * Contains:
 * - parseOrDefaultSchedule / getSchedulesOrDefault — schedule form values
 * - formatSchedule — schedule form values → payload row
 * - buildOnOffsetRule / buildOffOffsetRule — yup sun-offset rules
 * - buildRelayInitialValues — unit snapshot → Formik initial values
 * - generateRelayConfigPayload — form values diff → save payload
 * - canDiffRelayConfig — whether values/initialValues are positionally
 *   aligned for generateRelayConfigPayload
 * - collectDeletedLoadIds — deleted-load bookkeeping for onSaved callbacks
 * - applyPanelTypeChange / applyRelayPairing — relay pairing transitions
 */
import { addDays, addHours, format, parse, parseISO, startOfDay } from 'date-fns';
import { produce } from 'immer';
import * as yup from 'yup';

import { getLoadTypeCapabilities } from '@sophocles/features/reference/loadTypeCapabilities';
import { convertDayOfWeekArrayToNumber, convertDayOfWeekNumberToArray } from '@sophocles/utils/dataUtils';
import { buildRelayMonitorPayload } from '@sophocles/views/sites/components/SiteDashboard/relayMonitorConfig';

// ── Schedule form values ──────────────────────────────────────────────────────

export function parseOrDefaultSchedule(supportsNegativeOffsets, { schedule, scheduleNumber }) {
  const dateAt7PM = addHours(startOfDay(new Date()), 19);
  const dateAtMidnight = addDays(startOfDay(dateAt7PM), 1);

  if (schedule != null) {
    return {
      id: schedule.id ?? null,
      scheduleNumber: schedule?.schedule_number ?? scheduleNumber,
      enabled: schedule.enabled ?? false,
      onType: schedule.on_type ?? 'time',
      offType: schedule.off_type ?? 'time',
      onOffset: (supportsNegativeOffsets ? schedule.on_offset : -1 * Math.abs(schedule.on_offset)) ?? 0,
      offOffset: (supportsNegativeOffsets ? schedule.off_offset : Math.abs(schedule.off_offset)) ?? 0,
      onTime: schedule.on_time != null ? parse(schedule.on_time, 'HH:mm:ss', new Date()) : dateAt7PM,
      offTime: schedule.off_time != null ? parse(schedule.off_time, 'HH:mm:ss', new Date()) : dateAtMidnight,
      daysOfWeek: schedule.days_of_week ? convertDayOfWeekNumberToArray(schedule.days_of_week) : [0, 1, 2, 3, 4, 5, 6],
      dutyCycle: schedule.duty_cycle ?? 100,
      frequency: schedule.frequency ?? 1,
      isAlwaysOn: schedule.always_on ?? false,
    };
  }
  return {
    id: null,
    scheduleNumber,
    enabled: false,
    offType: 'time',
    onType: 'time',
    onTime: dateAt7PM,
    offTime: dateAtMidnight,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    dutyCycle: 100,
    frequency: 1,
    isAlwaysOn: false,
  };
}

export function getSchedulesOrDefault(data, supportsNegativeOffsets = false, maxSchedules = 2) {
  const scheduleCount = maxSchedules < data.length && data.length > 0 ? data.length : maxSchedules;
  const schedules = [];
  for (let i = 0; i < scheduleCount; i++) {
    if (i < data.length) {
      schedules.push(parseOrDefaultSchedule(supportsNegativeOffsets, { schedule: data[i] }));
    } else {
      schedules.push(parseOrDefaultSchedule(supportsNegativeOffsets, { scheduleNumber: i + 1 }));
    }
  }
  return schedules.sort((a, b) => (a.scheduleNumber > b.scheduleNumber ? 1 : -1));
}

// ── Payload row formatters ────────────────────────────────────────────────────

export const formatSchedule = (action, s, relayId, supportsNegativeOffsets = false) => ({
  action,
  id: s.id,
  schedule_number: s.scheduleNumber,
  relay: relayId,
  enabled: s.enabled,
  days_of_week: convertDayOfWeekArrayToNumber(s.daysOfWeek),
  always_on: s.isAlwaysOn,
  duty_cycle: s.dutyCycle,
  frequency: s.frequency,
  on_time: format(s.onTime, 'HH:mm:ss'),
  off_time: format(s.offTime, 'HH:mm:ss'),
  on_type: s.onType,
  on_offset: supportsNegativeOffsets ? s.onOffset : -1 * Math.abs(s.onOffset),
  off_type: s.offType,
  off_offset: supportsNegativeOffsets ? s.offOffset : Math.abs(s.offOffset),
});

const formatLoad = (action, r, rl) => ({
  action,
  id: rl.id,
  fixture_id: rl.lightFixture,
  name: rl.name,
  description: rl.description,
  direction: rl.direction,
  number_of_fixtures: rl.fixtureQuantity,
  primary_relay_id: r.id,
});

const formatLoadContract = (action, c, loadId) => ({
  action,
  id: c.id,
  relay_load: loadId,
  contract_start: c.start != null ? format(parseISO(c.start), 'yyyy-MM-dd') : null,
  contract_end: c.end != null ? format(parseISO(c.end), 'yyyy-MM-dd') : null,
  override_end_date: c.override,
  advertiser: c.advertiser,
});

// ── Yup validation builders ───────────────────────────────────────────────────

export const buildOnOffsetRule = (supportsNegativeOffsets, t) => {
  if (supportsNegativeOffsets) {
    return yup.number().required().min(-240, t('validation.between0And240')).max(240, t('validation.between0And240'));
  }
  return yup
    .number()
    .required()
    .integer()
    .min(-240, t('validation.between0And240'))
    .max(0, t('validation.between0And240'));
};

export const buildOffOffsetRule = (supportsNegativeOffsets, t) => {
  if (supportsNegativeOffsets) {
    return yup.number().required().min(-240, t('validation.between0And240')).max(240, t('validation.between0And240'));
  }
  return yup
    .number()
    .required()
    .integer()
    .min(0, t('validation.between0And240'))
    .max(240, t('validation.between0And240'));
};

// ── Snapshot → initial values ─────────────────────────────────────────────────

/**
 * Builds the Formik `relays` initial values from the odl-app unit snapshot.
 *
 * Maps renamed/inverted fields from the snapshot contract to form field names:
 *   load_type_id -> loadType, secondary_relay_id -> secondaryId,
 *   scheduling_enabled (!inverted) -> schedulesDisabled,
 *   loads[].fixture_id -> lightFixture, loads[].contracts[0] -> contract.
 *
 * @param {Array}   snapshotRelays - unit.relays from selectSiteUnit
 * @param {object}  options
 * @param {object}  [options.configuration] - unit.configuration (relay_function / relay_pulse_time)
 * @param {boolean} options.isCommercialUnit
 * @param {boolean} options.supportsNegativeOffsets
 * @param {number}  options.maxSchedules
 * @param {boolean} [options.includePolarity] - RelayBlade: carry relay polarity for the relay monitor
 * @param {boolean} [options.assignPairedLoadsToPrimary] - EditSchedulesDialog: re-home a paired
 *   secondary's loads onto its primary relay
 * @param {?number} [options.defaultLoadTypeId] - the in-scope CustomerType's default load-type id
 *   (CustomerType.default_load_type), used only when a relay has no saved load_type_id. Falls back to
 *   the literal isCommercialUnit ? 6 : 1 when absent (caller has no scope, e.g. EditSchedulesDialog).
 * @param {object} [options.loadTypeCapsById] - RelayLoadType capability flags keyed by id
 *   (selectRelayLoadTypeCapsById), resolved per relay via getLoadTypeCapabilities. Drives the triface
 *   threshold default and the fixture-support gate; an id absent from the map falls back to safe
 *   defaults (non-triface, fixtures supported).
 * @returns {Array} sorted relay form values, paired secondaries marked hidden
 */
export function buildRelayInitialValues(
  snapshotRelays,
  {
    configuration = null,
    isCommercialUnit,
    supportsNegativeOffsets,
    maxSchedules,
    includePolarity = false,
    assignPairedLoadsToPrimary = false,
    defaultLoadTypeId = null,
    loadTypeCapsById = {},
  },
) {
  const relayById = snapshotRelays.reduce((acc, r) => {
    acc[r.id] = r;
    return acc;
  }, {});

  const defaultRelayValues = [];
  const pairedIds = [];

  snapshotRelays
    .slice()
    .sort((a, b) => a.relay_number - b.relay_number)
    .forEach(r => {
      const loads = r.loads ?? [];
      const schedules = r.schedules ?? [];

      const initialRelayLoadType = r.load_type_id ?? defaultLoadTypeId ?? (isCommercialUnit ? 6 : 1);
      const initialRelayThreshold =
        r.threshold ?? (getLoadTypeCapabilities(loadTypeCapsById, initialRelayLoadType).is_triface_motor ? 50 : 180);

      // secondary_relay_id in snapshot is the raw FK id; look up relay_number.
      const secondaryId = r.secondary_relay_id ?? 0;
      const secondaryRelay = secondaryId ? relayById[secondaryId] : null;
      const secondaryNumber = secondaryRelay?.relay_number ?? null;

      defaultRelayValues.push({
        id: r.id,
        relayNumber: r.relay_number,
        secondaryId,
        secondaryNumber,
        // scheduling_enabled (positive in snapshot) → schedulesDisabled (negative in form)
        schedulesDisabled: !r.scheduling_enabled,
        loadType: initialRelayLoadType,
        threshold: initialRelayThreshold,
        powerMeasurementEnabled: r.power_measurement_active,
        normallyClosed: r.normally_closed,
        ...(includePolarity ? { polarity: r.polarity ?? false } : {}),
        ignoreCharting: r.ignore_charting ?? false,
        voltage: r.voltage,
        hidden: false,
        loads: [],
        schedules: getSchedulesOrDefault(schedules, supportsNegativeOffsets, maxSchedules),
        relayFunction: configuration?.relay_function != null ? configuration.relay_function[r.relay_number - 1] : 0,
        pulseTime: configuration?.relay_pulse_time != null ? configuration.relay_pulse_time[r.relay_number - 1] : 2,
      });

      if (secondaryId) {
        pairedIds.push(secondaryId);
      }

      // EditSchedulesDialog drift: when this relay is the secondary of an
      // already-built primary, its loads render (and save) under the primary.
      const parentRelayIndex = assignPairedLoadsToPrimary
        ? defaultRelayValues.findIndex(rv => rv.secondaryId === r.id)
        : -1;

      loads.forEach(rl => {
        // Pick the first contract for this load (contracts[] array in snapshot).
        const contract = rl.contracts?.[0] ?? null;
        const load = {
          id: rl.id ?? null,
          name: rl.name ?? '',
          description: rl.description ?? '',
          direction: rl.direction ?? '',
          // fixture_id in snapshot → lightFixture in form. Defaults to the
          // "Standard" fixture (pk 1) when a load has none, since the
          // RelayLoad.fixture FK is required.
          lightFixture: rl.fixture_id ?? 1,
          fixtureQuantity: rl.number_of_fixtures ?? 2,
          hasFixture: getLoadTypeCapabilities(loadTypeCapsById, r.load_type_id ?? 0).supports_light_fixtures,
          isOpen: false,
          contract:
            contract != null
              ? {
                  id: contract.id,
                  start: contract.contract_start,
                  end: contract.contract_end,
                  override: contract.override_end_date,
                  advertiser: contract.advertiser,
                }
              : null,
        };

        if (parentRelayIndex !== -1) {
          defaultRelayValues[parentRelayIndex].loads.push(load);
        } else {
          const existingIndex = defaultRelayValues.findIndex(rv => rv.id === r.id);
          if (existingIndex !== -1) {
            defaultRelayValues[existingIndex].loads.push(load);
          }
        }
      });
    });

  for (let i = 0; i < defaultRelayValues.length; i++) {
    if (pairedIds.includes(defaultRelayValues[i].id)) {
      defaultRelayValues[i].hidden = true;
    }
  }

  return defaultRelayValues.sort((a, b) => a.relayNumber - b.relayNumber);
}

// ── Save payload diff builder ─────────────────────────────────────────────────

/**
 * Diffs form values against initial values into the site-relay-config payload.
 *
 * Relay-monitor handling is gated on input presence: when `values.relayMonitor`
 * is absent (EditSchedulesDialog) no relay_monitor / polarity keys are emitted.
 *
 * @param {object}  values        - current Formik values ({ relays, [relayMonitor] })
 * @param {object}  initialValues - Formik initial values of the same shape
 * @param {object}  options
 * @param {boolean} [options.supportsNegativeOffsets]
 * @param {boolean} [options.useLooseEquality] - EditSchedulesDialog diffs with `==`, RelayBlade with `===`
 * @param {object} [options.loadTypeCapsById] - RelayLoadType capability flags keyed by id
 *   (selectRelayLoadTypeCapsById); resolves which relay is a paired-latching primary when
 *   borrowing its pulse time for the secondary.
 * @returns {{ configPayload: object, contractRows: Array }}
 *
 * Invariants:
 * - `values.relays` and `initialValues.relays` must be the same length and
 *   positional order (their positional `initialValues.relays[ri]` lookups are
 *   unchecked) — callers gate on `canDiffRelayConfig` first.
 */
export function generateRelayConfigPayload(
  values,
  initialValues,
  { supportsNegativeOffsets = false, useLooseEquality = false, loadTypeCapsById = {} } = {},
) {
  const changed = useLooseEquality ? (a, b) => a != b : (a, b) => a !== b;
  const equals = (a, b) => !changed(a, b);

  const configPayload = {
    actions: [{ loads: [], relays: [], schedules: [] }],
  };
  const contractRows = [];

  let updateSmartLinkParameters = false;
  const params = { relay_function: [], relay_pulse_time: [] };

  values.relays.forEach((r, ri) => {
    params.relay_function.push(r.relayFunction);
    // For paired relays (latching), use the primary relay's pulse time for both
    const primaryIndex = values.relays.findIndex(
      pr =>
        pr.secondaryNumber === r.relayNumber && getLoadTypeCapabilities(loadTypeCapsById, pr.loadType).supports_pairing,
    );
    const pulseTime = primaryIndex >= 0 ? values.relays[primaryIndex].pulseTime : r.pulseTime;
    params.relay_pulse_time.push(pulseTime);

    if (
      changed(r.relayFunction, initialValues.relays[ri].relayFunction) ||
      changed(r.pulseTime, initialValues.relays[ri].pulseTime)
    ) {
      updateSmartLinkParameters = true;
    }
  });

  if (updateSmartLinkParameters) {
    configPayload.relaycontroller = { action: 'modify', params };
  }

  const relayMonitorPayload =
    values.relayMonitor != null
      ? buildRelayMonitorPayload(values, initialValues)
      : { relayMonitor: null, relayActions: [] };
  if (relayMonitorPayload.relayMonitor != null) {
    configPayload.relay_monitor = relayMonitorPayload.relayMonitor;
  }
  const relayMonitorActionsById = relayMonitorPayload.relayActions.reduce((map, action) => {
    map[action.id] = action;
    return map;
  }, {});

  values.relays.forEach((r, ri) => {
    const init = initialValues.relays[ri];

    // Loads
    r.loads.forEach((rl, rli) => {
      let action = 'modify';
      if (rl.id == null) action = 'create';
      if (JSON.stringify(rl) === JSON.stringify(init.loads[rli])) action = 'none';
      if (action !== 'none') {
        configPayload.actions[0].loads.push(formatLoad(action, r, rl));
        if (rl.contract != null) {
          contractRows.push(formatLoadContract(action, rl.contract, rl.id));
        }
      }
    });

    // Deleted loads
    init.loads.forEach(rl => {
      const allCurrentLoadIds = values.relays.reduce((acc, cur) => {
        cur.loads.forEach(l => {
          if (l.id) acc.push(l.id);
        });
        return acc;
      }, []);
      if (!allCurrentLoadIds.includes(rl.id)) {
        configPayload.actions[0].loads.push(formatLoad('delete', r, rl));
      }
    });

    // Relay settings
    const relayAction = { id: r.id };

    if (changed(r.powerMeasurementEnabled, init.powerMeasurementEnabled)) {
      relayAction.power_measurement_active = r.powerMeasurementEnabled;
    }
    if (changed(r.ignoreCharting, init.ignoreCharting)) {
      relayAction.ignore_charting = r.ignoreCharting;
    }
    if (changed(r.loadType, init.loadType)) {
      relayAction.load_type = r.loadType;
    }

    // Preserve scheduling_enabled ↔ normally_closed coupling:
    // enabling schedules forces normally_closed = false (relays.py:961-962).
    // NOTE: send scheduling_enabled (positive) per the new contract, NOT all_schedules_deactivated.
    let isEnablingSchedules = false;
    if (changed(r.schedulesDisabled, init.schedulesDisabled)) {
      relayAction.scheduling_enabled = !r.schedulesDisabled;
      if (!r.schedulesDisabled) isEnablingSchedules = true;
    }
    if (isEnablingSchedules) {
      relayAction.normally_closed = false;
    } else if (changed(r.normallyClosed, init.normallyClosed)) {
      relayAction.normally_closed = r.normallyClosed;
    }

    if (changed(r.secondaryId, init.secondaryId)) {
      relayAction.secondary_relay = equals(r.secondaryId, 0) ? null : r.secondaryId;
    }
    if (changed(r.threshold, init.threshold)) {
      relayAction.threshold = r.threshold;
    }
    if (changed(r.pulseTime, init.pulseTime)) {
      relayAction.pulseTime = r.pulseTime;
    }
    if (changed(r.voltage, init.voltage)) {
      relayAction.voltage = r.voltage;
    }
    if (changed(r.relayFunction, init.relayFunction)) {
      relayAction.relayFunction = r.relayFunction;
    }

    const relayMonitorAction = relayMonitorActionsById[r.id];
    if (relayMonitorAction) {
      relayAction.secondary_relay = relayMonitorAction.secondary_relay;
      relayAction.polarity = relayMonitorAction.polarity;
    }

    if (Object.keys(relayAction).length > 1) {
      relayAction.action = 'modify';
      configPayload.actions[0].relays.push(relayAction);
    }

    // Schedules
    r.schedules.forEach((s, si) => {
      let action = 'modify';
      if (s.id == null) action = 'create';
      if (!init.schedules.map(ivs => ivs.id).includes(s.id)) action = 'delete';
      if (JSON.stringify(s) === JSON.stringify(init.schedules[si])) action = 'none';
      if (action !== 'none') {
        configPayload.actions[0].schedules.push(formatSchedule(action, s, r.id, supportsNegativeOffsets));
      }
    });

    // Deleted schedules
    init.schedules.forEach(s => {
      if (!r.schedules.map(cs => cs.id).includes(s.id)) {
        configPayload.actions[0].schedules.push(formatSchedule('delete', s, r.id, supportsNegativeOffsets));
      }
    });
  });

  return { configPayload, contractRows };
}

/**
 * Whether `values` and `initialValues` are positionally aligned for
 * generateRelayConfigPayload's `initialValues.relays[ri]` lookups.
 *
 * False during the one-render window where a Formik `enableReinitialize`
 * consumer's `initialValues` prop has already changed but its own `values`
 * hasn't caught up yet (or vice versa) — e.g. RelayBlade resetting its local
 * initialValues to an empty relays array on close while Formik's values still
 * hold the prior snapshot.
 *
 * @returns {boolean}
 */
export function canDiffRelayConfig(values, initialValues) {
  return values.relays.length === initialValues.relays.length;
}

/** Ids of persisted loads the payload deletes — for onSaved({ deletedLoadIds }) bookkeeping. */
export function collectDeletedLoadIds(configPayload) {
  return configPayload.actions[0].loads.filter(l => l.action === 'delete' && l.id != null).map(l => l.id);
}

// ── Relay pairing transitions ─────────────────────────────────────────────────

/**
 * Applies a panel/load-type change to the relay form values.
 *
 * Selecting a latching type (7/8) pairs relays 1+2 or 3+4 (primary gets the
 * type, relayFunction 1 and a default pulse time; the secondary is hidden with
 * relayFunction 2). Selecting any other type un-latches a paired relay pair.
 *
 * @param {?number} [options.defaultLoadTypeId] - the in-scope CustomerType's default load-type id,
 *   used when un-latching a paired relay. Falls back to the literal isCommercialUnit ? 6 : 1 when absent.
 * @param {object} [options.loadTypeCapsById] - RelayLoadType capability flags keyed by id
 *   (selectRelayLoadTypeCapsById); gates the pairing branch on the chosen panelType's
 *   supports_pairing flag. The write still assigns panelType itself (the option's own id).
 * @returns {Array|null} the next relays array, or null when nothing changes
 */
export function applyPanelTypeChange(
  relays,
  relayIndex,
  panelType,
  { isCommercialUnit, defaultLoadTypeId = null, loadTypeCapsById = {} },
) {
  if (getLoadTypeCapabilities(loadTypeCapsById, panelType).supports_pairing) {
    const pairByPosition = (primaryIdx, secondaryIdx) =>
      produce(relays, draft => {
        draft[primaryIdx].loadType = panelType;
        draft[primaryIdx].relayFunction = 1;
        draft[primaryIdx].pulseTime = draft[primaryIdx].pulseTime || 2;
        draft[primaryIdx].secondaryId = draft[secondaryIdx].id;
        draft[primaryIdx].secondaryNumber = draft[secondaryIdx].relayNumber;
        draft[secondaryIdx].relayFunction = 2;
        draft[secondaryIdx].hidden = true;
      });

    switch (relays[relayIndex].relayNumber) {
      case 1:
      case 2:
        return pairByPosition(0, 1);
      case 3:
      case 4:
        return pairByPosition(2, 3);
      default:
        return null;
    }
  }

  // Un-latching: if this relay was paired, release the secondary
  const relay = relays[relayIndex];
  if (!relay.secondaryId || relay.secondaryId === 0 || relay.secondaryNumber == null) return null;
  const pairedIdx = relays.findIndex(rv => rv.id === relay.secondaryId);
  if (pairedIdx === -1) return null;

  const unlatchedLoadType = defaultLoadTypeId ?? (isCommercialUnit ? 6 : 1);

  return produce(relays, draft => {
    draft[pairedIdx].hidden = false;
    draft[pairedIdx].relayFunction = 0;
    draft[pairedIdx].loadType = unlatchedLoadType;
    draft[pairedIdx].secondaryId = 0;
    draft[pairedIdx].secondaryNumber = null;
    draft[relayIndex].relayFunction = 0;
    draft[relayIndex].secondaryId = 0;
    draft[relayIndex].secondaryNumber = null;
  });
}

/**
 * Applies a pair/unpair selection from the paired-relay field.
 *
 * A non-zero target pairs it under this relay (target hidden); zero unpairs
 * (target un-hidden, this relay's secondaryNumber cleared).
 *
 * @returns {Array|null} the next relays array, or null when nothing changes
 */
export function applyRelayPairing(relays, relayIndex, targetRelayId) {
  if (targetRelayId !== 0) {
    const pairedIdx = relays.findIndex(rv => rv.id === targetRelayId);
    return produce(relays, draft => {
      draft[relayIndex].secondaryId = draft[pairedIdx].id;
      draft[relayIndex].secondaryNumber = draft[pairedIdx].relayNumber;
      draft[pairedIdx].hidden = true;
    });
  }

  const relay = relays[relayIndex];
  if (relay.secondaryId !== 0 && relay.secondaryNumber != null) {
    const pairedIdx = relays.findIndex(rv => rv.id === relay.secondaryId);
    return produce(relays, draft => {
      if (pairedIdx !== -1) draft[pairedIdx].hidden = false;
      draft[relayIndex].secondaryNumber = null;
    });
  }
  return null;
}
